import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, map, of, switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { StateCleanupService } from './state-cleanup.service';

/** Structured Moodle WS error returned by al-filemanager when Moodle itself
 *  reports an exception (HTTP 422). */
export interface MoodleErrorDetail {
  error_code: string;
  moodle_errorcode?: string;
  message?: string;
}

export interface MoodleCourse {
  id: number;
  canonical_id?: string;
  registration_id?: string;
  registration_selectable?: boolean;
  disabled_reason?: string | null;
  site_name?: string | null;
  site_url?: string | null;
  site_identifier?: string | null;
  fullname: string;
  shortname: string;
  summary: string;
  startdate?: number;
  enddate?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total?: number;
  count?: number;
  limit?: number;
  offset?: number;
  has_more?: boolean;
  pagination?: { total: number; limit: number; offset: number };
}

export interface MoodleSyncJob {
  id: string;
  course_id: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  progress: number;
  total_items: number;
  processed_items: number;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
}

export interface MoodleBulkSyncJob {
  id: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  total_courses: number;
  completed_courses: number;
  failed_courses: number;
  current_course_name?: string;
  progress: number;
  course_jobs?: Array<{
    moodle_course_id: number;
    course_name: string;
    status: string;
    error_message?: string;
  }>;
  trigger_type: string;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
}

export interface MoodleSyncSettings {
  sync_frequency: 'NEVER' | 'DAILY' | 'MONTHLY' | 'YEARLY';
  last_auto_sync?: string;
}

export interface MoodleCategory {
  id: number;
  name: string;
  parent: number;
}

export interface MoodlePushResult {
  moodle_course_id: number;
  status: string;
  total_items: number;
  processed_items: number;
  warnings?: string[];
}

export interface CourseContentItem {
  id: string;
  name: string;
  module_type: string;
  status_embedding: boolean;
  has_content: boolean;
  has_file: boolean;
  position: number;
  /** Only set on scorm_package / h5p_activity items whose extraction failed
   *  ('empty', 'failed', 'missing_package', 'unsupported_dynamic_package');
   *  successfully extracted packages are not returned by the backend. */
  extraction_status?: string | null;
  /** Fehlertext der Einbettung, wenn sie fehlgeschlagen ist. Nur gesetzt,
   *  solange der Inhalt nicht eingebettet ist — er unterscheidet „hat nicht
   *  geklappt" von „läuft noch", was in der Anzeige sonst gleich aussieht. */
  embedding_error?: string | null;
}

export interface CourseSection {
  id: string;
  name: string;
  summary: string;
  position: number;
  items: CourseContentItem[];
}

export interface CourseStructure {
  id: string;
  course_name: string;
  description: string;
  source_type: string;
  sections: CourseSection[];
}

/** Wire shape of `GET /al/tutors/moodle/canonical/courses`.
 *  Distinct from `MoodleCourse` (legacy /al/files/moodle/courses live shape) —
 *  `id` here is the canonical UUID, `moodle_course_id` is the Moodle int. */
interface CanonicalMoodleCourse {
  id: string;
  registration_id: string;
  moodle_course_id: number;
  shortname?: string | null;
  fullname?: string | null;
  summary?: string | null;
  category_id?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  visible: boolean;
  last_synced_at?: string | null;
  participant_count?: number;
  registration_selectable?: boolean;
  disabled_reason?: string | null;
  site_name?: string | null;
  site_url?: string | null;
  site_identifier?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class MoodleService {
  private baseUrl = `${environment.filesBackend}`;
  private tutorsBaseUrl = `${environment.tutorsBackend}`;

  moodleCourses$ = new BehaviorSubject<MoodleCourse[]>([]);
  activeSyncJob$ = new BehaviorSubject<MoodleSyncJob | null>(null);
  activeBulkSyncJob$ = new BehaviorSubject<MoodleBulkSyncJob | null>(null);
  syncSettings$ = new BehaviorSubject<MoodleSyncSettings>({ sync_frequency: 'NEVER' });
  courseStructure$ = new BehaviorSubject<CourseStructure | null>(null);
  loading$ = new BehaviorSubject(false);

  private pollInterval: ReturnType<typeof setInterval> | null = null;
  private bulkPollInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private http: HttpClient,
    private stateCleanupService: StateCleanupService,
    private t: TranslateService
  ) {
    this.stateCleanupService.register(() => this.resetState());
  }

  /** Translate a Moodle WS error from al-filemanager into a localized,
   *  actionable hint. Falls back to the cleaned Moodle message for unknown
   *  errorcodes, and to the generic fallback if the response is not a
   *  structured Moodle error at all. */
  translateMoodleError(err: HttpErrorResponse | unknown, fallbackKey: string): string {
    const detail =
      err instanceof HttpErrorResponse ? (err.error?.detail as MoodleErrorDetail | undefined) : undefined;
    if (detail?.error_code === 'moodle_course_quota_exceeded') {
      return this.t.instant('dialogs.moodleErrors.moodle_course_quota_exceeded', {
        limit: (detail as any).quota_limit ?? 0,
        remaining: (detail as any).remaining ?? 0,
        requested: (detail as any).requested ?? 1,
      });
    }
    if (detail?.error_code && detail.error_code !== 'unknown') {
      return this.t.instant(`dialogs.moodleErrors.${detail.error_code}`);
    }
    if (detail?.message) {
      return this.t.instant('dialogs.moodleErrors.unknown', { message: detail.message });
    }
    return this.t.instant(fallbackKey);
  }

  /** Reason text for a course that failed inside a bulk course sync
   *  (`CourseSyncBulkStatus.course_errors`). `null` when the server sent no
   *  structured reason — the caller keeps its generic message. */
  translateCourseSyncError(
    error: { error_code?: string | null; message?: string | null } | null | undefined,
  ): string | null {
    const code = error?.error_code;
    if (!code) return null;
    const message = error?.message ?? '';
    if (code !== 'unknown') {
      const key = `dialogs.moodleErrors.${code}`;
      const text = this.t.instant(key, { message });
      if (text !== key) return text;
    }
    return message ? this.t.instant('dialogs.moodleErrors.unknown', { message }) : null;
  }

  /** Fetch available Moodle courses from the canonical layer.
   *
   *  Reads from `moodle_courses_canonical` (populated by the al-tutor
   *  refresh worker once per day, plus on-demand right after the plugin
   *  posts a fresh ws_token). Response is permission-filtered server-side:
   *  admin/manager → all client courses, dozent → only courses linked to
   *  bots they have `user_bot_access` on.
   *
   *  Maps the canonical wire shape to the legacy `MoodleCourse` interface
   *  so downstream UI code (sync dialogs, picker, etc.) keeps working.
   *  `id` remains Moodle's int ID for backwards compatibility; the canonical
   *  UUID and registration ID are kept to disambiguate equal course IDs across
   *  multiple Moodle instances during imports and deletes. */
  listMoodleCoursesPage(opts?: { limit?: number; offset?: number; search?: string }) {
    this.loading$.next(true);
    let params = new HttpParams();
    if (opts?.limit != null) params = params.set('limit', String(opts.limit));
    if (opts?.offset != null) params = params.set('offset', String(opts.offset));
    if (opts?.search) params = params.set('search', opts.search);
    return this.http
      .get<PaginatedResponse<CanonicalMoodleCourse>>(
        `${this.tutorsBaseUrl}/moodle/canonical/courses`,
        { params },
      )
      .pipe(
        map((res) => ({
          ...res,
          data: (res.data || []).map<MoodleCourse>((c) => ({
            id: c.moodle_course_id,
            canonical_id: c.id,
            registration_id: c.registration_id,
            registration_selectable: c.registration_selectable,
            disabled_reason: c.disabled_reason,
            site_name: c.site_name,
            site_url: c.site_url,
            site_identifier: c.site_identifier,
            fullname: c.fullname ?? '',
            shortname: c.shortname ?? '',
            summary: c.summary ?? '',
            startdate: c.start_date ? Math.floor(new Date(c.start_date).getTime() / 1000) : undefined,
            enddate: c.end_date ? Math.floor(new Date(c.end_date).getTime() / 1000) : undefined,
          })),
        })),
        tap((res) => {
          this.moodleCourses$.next(res.data);
          this.loading$.next(false);
        }),
      );
  }

  private collectMoodleCoursePages(limit = 100, offset = 0, acc: MoodleCourse[] = []): Observable<MoodleCourse[]> {
    return this.listMoodleCoursesPage({ limit, offset }).pipe(
      switchMap((res) => {
        const data = res.data ?? [];
        const next = [...acc, ...data];
        const hasMore = res.has_more ?? ((res.total ?? res.pagination?.total ?? next.length) > offset + data.length);
        return hasMore ? this.collectMoodleCoursePages(limit, offset + limit, next) : of(next);
      })
    );
  }

  listMoodleCourses() {
    return this.collectMoodleCoursePages().pipe(map((courses) => ({ data: courses })));
  }

  /** Force the al-tutor canonical refresh worker to re-pull this
   *  registration's courses on the next tick (≤5 min). Use after the
   *  admin connects a fresh Moodle plugin or wants newly-created
   *  Moodle courses to appear without waiting for the daily cron. */
  refreshCanonicalMoodleCourses(registrationId: string) {
    return this.http
      .post<{ registration_id: string; queued_kinds: string[]; next_due_at: string }>(
        `${this.tutorsBaseUrl}/moodle/canonical/refresh/${registrationId}`,
        {}
      );
  }

  /** Start a Moodle course sync */
  startSync(
    moodleCourseId: number,
    courseName: string,
    bucketId?: string,
    syncFrequency?: string,
    registrationId?: string,
    moodleCourseUuid?: string
  ) {
    const body: any = {
      moodle_course_id: moodleCourseId,
      course_name: courseName,
    };
    if (bucketId) {
      body.bucket_id = bucketId;
    }
    if (syncFrequency) {
      body.sync_frequency = syncFrequency;
    }
    if (registrationId) {
      body.registration_id = registrationId;
    }
    if (moodleCourseUuid) {
      body.moodle_course_uuid = moodleCourseUuid;
    }

    return this.http
      .post<{ job_id: string; status: string; message: string }>(
        `${this.baseUrl}/moodle/sync`,
        body
      )
      .pipe(
        tap((res) => {
          this.activeSyncJob$.next({
            id: res.job_id,
            course_id: '',
            status: res.status as MoodleSyncJob['status'],
            progress: 0,
            total_items: 0,
            processed_items: 0,
          });
          this.startPolling(res.job_id);
        })
      );
  }

  /** Get sync job status */
  getSyncStatus(jobId: string) {
    return this.http
      .get<{ data: MoodleSyncJob }>(`${this.baseUrl}/moodle/sync/${jobId}`)
      .pipe(map((res) => res.data));
  }

  /** Start polling for sync status */
  startPolling(jobId: string) {
    this.stopPolling();
    this.pollInterval = setInterval(() => {
      this.getSyncStatus(jobId).subscribe({
        next: (job) => {
          this.activeSyncJob$.next(job);
          if (job.status === 'COMPLETED' || job.status === 'FAILED') {
            this.stopPolling();
          }
        },
        error: () => this.stopPolling(),
      });
    }, 3000);
  }

  /** Stop polling */
  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  /** Get course structure (sections + items) */
  getCourseStructure(courseId: string) {
    this.loading$.next(true);
    return this.http
      .get<{ data: CourseStructure }>(`${this.baseUrl}/courses/${courseId}/structure`)
      .pipe(
        tap((res) => {
          this.courseStructure$.next(res.data);
          this.loading$.next(false);
        }),
        map((res) => res.data)
      );
  }

  /** Delete all synced content for a course */
  deleteCourseContent(courseId: string) {
    return this.http.delete(`${this.baseUrl}/courses/${courseId}/content`);
  }

  /** Delete a course and all its content, embeddings, and files */
  deleteCourse(courseId: string) {
    return this.http.delete(`${this.baseUrl}/course/${courseId}`);
  }

  /** Start a bulk sync for multiple courses */
  startBulkSync(
    courses: Array<{
      moodle_course_id: number;
      course_name: string;
      registration_id?: string;
      moodle_course_uuid?: string;
    }>,
    bucketId?: string,
    syncFrequency?: string
  ) {
    const body: any = { courses };
    if (bucketId) {
      body.bucket_id = bucketId;
    }
    if (syncFrequency) {
      body.sync_frequency = syncFrequency;
    }

    return this.http
      .post<{ data: { id: string; status: string; total_courses: number; message: string } }>(
        `${this.baseUrl}/moodle/sync/bulk`,
        body
      )
      .pipe(
        tap((res) => {
          this.activeBulkSyncJob$.next({
            id: res.data.id,
            status: res.data.status as MoodleBulkSyncJob['status'],
            total_courses: res.data.total_courses,
            completed_courses: 0,
            failed_courses: 0,
            progress: 0,
            trigger_type: 'manual',
          });
          this.startBulkPolling(res.data.id);
        })
      );
  }

  /** Get bulk sync job status */
  getBulkSyncStatus(jobId: string) {
    return this.http
      .get<{ data: MoodleBulkSyncJob }>(`${this.baseUrl}/moodle/sync/bulk/${jobId}`)
      .pipe(map((res) => res.data));
  }

  /** Start polling for bulk sync status */
  startBulkPolling(jobId: string) {
    this.stopBulkPolling();
    this.bulkPollInterval = setInterval(() => {
      this.getBulkSyncStatus(jobId).subscribe({
        next: (job) => {
          this.activeBulkSyncJob$.next(job);
          if (job.status === 'COMPLETED' || job.status === 'FAILED') {
            this.stopBulkPolling();
          }
        },
        error: () => this.stopBulkPolling(),
      });
    }, 3000);
  }

  /** Stop bulk polling */
  stopBulkPolling() {
    if (this.bulkPollInterval) {
      clearInterval(this.bulkPollInterval);
      this.bulkPollInterval = null;
    }
  }

  /** Get sync settings */
  getSyncSettings(registrationId?: string | null) {
    const options = registrationId
      ? { params: new HttpParams().set('registration_id', registrationId) }
      : {};
    return this.http
      .get<MoodleSyncSettings>(`${this.baseUrl}/moodle/sync/settings`, options)
      .pipe(tap((settings) => this.syncSettings$.next(settings)));
  }

  /** Update sync frequency */
  updateSyncSettings(
    syncFrequency: 'NEVER' | 'DAILY' | 'MONTHLY' | 'YEARLY',
    registrationId?: string | null
  ) {
    const options = registrationId
      ? { params: new HttpParams().set('registration_id', registrationId) }
      : {};
    return this.http
      .put<MoodleSyncSettings>(
        `${this.baseUrl}/moodle/sync/settings`,
        { sync_frequency: syncFrequency },
        options
      )
      .pipe(tap((settings) => this.syncSettings$.next(settings)));
  }

  /** Get full course details */
  getCourseDetail(courseId: string) {
    return this.http
      .get<{ data: any }>(`${this.baseUrl}/courses/${courseId}`)
      .pipe(map((res) => res.data));
  }

  /** Update course settings */
  updateCourse(courseId: string, data: {
    description?: string;
    metadata_context?: string;
    learning_goals?: string;
    difficulty_level?: string;
    sync_frequency?: string;
    participant_sync_frequency?: string;
    dependency_ids?: string[];
  }) {
    return this.http
      .put<{ data: any }>(`${this.baseUrl}/courses/${courseId}`, data)
      .pipe(map((res) => res.data));
  }

  /** Get all courses for the client (for dependency picker) */
  getClientCourses() {
    return this.http
      .get<{ data: Array<{ id: string; course_name: string }> }>(`${this.baseUrl}/courses/client-courses`)
      .pipe(map((res) => res.data));
  }

  /** Get Moodle categories for push target selection */
  /** Resolve the local synced course row for a Moodle course (placements
   *  row menu → synced-content overview). 404 detail "no_synced_course"
   *  when the sync never created a local course yet. */
  lookupSyncedCourse(registrationId: string, moodleCourseId: number, moodleCourseUuid?: string) {
    let params = new HttpParams()
      .set('moodle_course_id', String(moodleCourseId))
      .set('registration_id', registrationId);
    if (moodleCourseUuid) {
      params = params.set('moodle_course_uuid', moodleCourseUuid);
    }
    return this.http
      .get<{ data: { course_id: string; course_name: string } }>(
        `${this.baseUrl}/moodle/sync/course-lookup`,
        { params },
      )
      .pipe(map((res) => res.data));
  }

  getMoodleCategories(courseId?: string, registrationId?: string) {
    let params = new HttpParams();
    if (courseId) {
      params = params.set('course_id', courseId);
    }
    if (registrationId) {
      params = params.set('registration_id', registrationId);
    }
    return this.http
      .get<{ data: MoodleCategory[] }>(`${this.baseUrl}/moodle/categories`, { params })
      .pipe(map((res) => res.data));
  }

  /** Push a course to the linked Moodle instance */
  pushToMoodle(courseId: string, categoryId?: number, shortname?: string, courseName?: string, registrationId?: string) {
    const body: any = {};
    if (categoryId !== undefined) {
      body.category_id = categoryId;
    }
    if (courseName) {
      body.course_name = courseName;
    }
    if (shortname) {
      body.shortname = shortname;
    }
    if (registrationId) {
      body.registration_id = registrationId;
    }
    return this.http
      .post<MoodlePushResult>(
        `${this.baseUrl}/courses/${courseId}/push-to-moodle`,
        body
      );
  }

  private resetState() {
    this.moodleCourses$.next([]);
    this.activeSyncJob$.next(null);
    this.activeBulkSyncJob$.next(null);
    this.syncSettings$.next({ sync_frequency: 'NEVER' });
    this.courseStructure$.next(null);
    this.loading$.next(false);
    this.stopPolling();
    this.stopBulkPolling();
  }
}
